import React, { useState } from 'react';
import { Application } from '../../../../../../entities/Application';
import { getLabelForCount } from '../../../../../../utils/grammar';
import { CloseButton } from './CloseButton';
import { TabHeaders } from './TabHeaders';
import { ApplicationActions } from './ApplicationActions';
import { ApplicationQuestions } from '../ApplicationQuestions';
import { Resume } from '../Resume';
import { AlertObject } from '../../../../../../components/Alert';
import { BULK_REVIEW_NAVIGATION_TYPE } from '../../..';
import styles from '../../styles.module.scss';
import classNames from 'classnames';
import { RedactingLoadingState } from '../../../../RedactingLoadingState';
import { NavigationButtons } from './NavigationButtons';
import { NotesPanel } from './NotesPanel';
import { User } from '../../../../../../entities/User';
import { AccountIntegration } from '../../../../../../entities/AccountIntegration';

export type BULK_REVIEW_TABS = 'resume' | 'questions';

interface PropTypes {
  application: Application;
  index: number;
  isLoadingResume: boolean;
  preSelectedRejectReasonId: number;
  preSelectedStageId: number;
  totalApplications: number;
  isActionsDisabled: boolean;
  resumeUrl?: string;
  currentUser: User;
  emailAccountIntegration: AccountIntegration;
  onNext: (navType: BULK_REVIEW_NAVIGATION_TYPE) => void;
  onPrevious: () => void;
  onClose: () => void;
  onConflict: () => void;
  onTagsAdded: () => void;
  setAlert: (alert: AlertObject) => void;
  setPreSelectedRejectReasonId: (id: number) => void;
  setPreSelectedStageId: (id: number) => void;
}

function Content(props: {
  activeTab: BULK_REVIEW_TABS;
  application: Application;
  isLoadingResume: boolean;
  resumeUrl?: string;
  reloadNotesKey: number;
  setAlert: (alert: AlertObject) => void;
  onNoteAdded: () => void;
}) {
  const loadingElement = (
    <RedactingLoadingState redacted={props.application.redacted} />
  );

  const mainContent =
    props.activeTab === 'resume' ? (
      <Resume
        isLoading={props.resumeUrl == null && props.isLoadingResume}
        loadingElement={loadingElement}
        url={props.resumeUrl || ''}
      />
    ) : (
      <ApplicationQuestions application={props.application} />
    );

  return (
    <div className='d-flex flex-grow-1 overflow-hidden'>
      <div
        className={classNames(
          'flex-grow-1 d-flex flex-column',
          styles['hidden-scrollbar'],
        )}
      >
        {mainContent}
      </div>
      <NotesPanel
        application={props.application}
        reloadKey={props.reloadNotesKey}
        setAlert={props.setAlert}
        onNoteAdded={props.onNoteAdded}
      />
    </div>
  );
}

function getCurrentIndex(index: number, totalApplications: number) {
  const currentIndex = index + 1;

  if (currentIndex > totalApplications) {
    return totalApplications;
  }

  return currentIndex;
}

export function BulkApplicationReviewHeader(props: PropTypes) {
  const [activeTab, setActiveTab] = useState<BULK_REVIEW_TABS>('resume');
  // Bumped after a note is added to force NotesPanel to refetch.
  const [reloadNotesKey, setReloadNotesKey] = useState<number>(0);

  const handleNoteAdded = () => setReloadNotesKey((key) => key + 1);

  return (
    <>
      <div className='border-bottom px-4 mt-4'>
        <CloseButton onClose={props.onClose} />
        <div className='fs-5 text-dark-200 flex-shrink-0'>
          Reviewing {getCurrentIndex(props.index, props.totalApplications)} out
          of {getLabelForCount('Candidate', props.totalApplications)}
        </div>
        <div className='d-flex mt-4 justify-content-between align-items-center'>
          <NavigationButtons
            index={props.index}
            onPrevious={props.onPrevious}
            onNext={props.onNext}
          />
          <ApplicationActions {...props} onNoteAdded={handleNoteAdded} />
        </div>
        <TabHeaders activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      <Content
        activeTab={activeTab}
        application={props.application}
        resumeUrl={props.resumeUrl}
        isLoadingResume={props.isLoadingResume}
        reloadNotesKey={reloadNotesKey}
        setAlert={props.setAlert}
        onNoteAdded={handleNoteAdded}
      />
    </>
  );
}
